import React, {useContext, useEffect, useMemo, useState} from "react";
import {Table, Pagination, message} from "antd";
import {UserContext} from "../UserContext";
import api from "../api/index";
import {useNavigate} from "react-router-dom";
import "./styles/PaymentSetup.css";

const LOG_PAGE_SIZE = 20;

const COMMON_FIELDS = [
    {name: "first_name", label: "First name", type: "text"},
    {name: "last_name", label: "Last name", type: "text"},
    {name: "email", label: "Email", type: "email"},
    {name: "phone", label: "Phone", type: "tel"},
    {name: "address", label: "Address", type: "text"},
    {name: "city", label: "City", type: "text"},
    {name: "state", label: "State", type: "text"},
    {name: "zipcode", label: "Zip code", type: "text"},
];

const TYPE_FIELDS = {
    ach: [
        {name: "account_number", label: "Account number", type: "text"},
        {name: "routing_number", label: "Routing number", type: "text"},
        {
            name: "account_type",
            label: "Account type",
            type: "select",
            options: ["CHECKING", "SAVINGS"],
        },
        {
            name: "payee_type",
            label: "Payee type",
            type: "select",
            options: ["INDIVIDUAL", "BUSINESS"],
        },
    ],
    debit: [
        {name: "card_number", label: "Card number", type: "text"},
        {name: "card_type", label: "Card type", type: "select", options: ["VISA", "MASTERCARD"]},
        {name: "name_on_card", label: "Name on card", type: "text"},
        {name: "exp_month", label: "Exp month (MM)", type: "text"},
        {name: "exp_year", label: "Exp year (YY)", type: "text"},
    ],
    paypal: [
        {name: "paypal_email", label: "PayPal email", type: "email"},
    ],
    venmo: [
        {name: "venmo_phone", label: "Venmo phone", type: "tel"},
    ],
};

const defaultForm = {
    payment_type: "ach",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipcode: "",
    account_number: "",
    routing_number: "",
    account_type: "",
    payee_type: "",
    card_number: "",
    card_type: "",
    name_on_card: "",
    exp_month: "",
    exp_year: "",
    paypal_email: "",
    venmo_phone: "",
};

const buildQuery = (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        search.set(key, String(value));
    });
    return search.toString();
};

const resolveSelectedId = (list = []) => {
    const selected = list.find((item) => Number(item?.selected) === 1);
    return selected?.id ?? null;
};

const formatStatus = (status) => (status ? status.replace(/_/g, " ") : "—");

const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "—";
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return amount;
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
    }).format(numeric / 100);
};

export default function PaymentSetup() {
    const {user, setUser} = useContext(UserContext);
    const navigate = useNavigate();
    const [methods, setMethods] = useState([]);
    const [methodsLoading, setMethodsLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [serverSelectedId, setServerSelectedId] = useState(null);
    const [savingSelection, setSavingSelection] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [submitting, setSubmitting] = useState(false);
    const [deletingIds, setDeletingIds] = useState([]);
    const [logItems, setLogItems] = useState([]);
    const [logLoading, setLogLoading] = useState(true);
    const [logPage, setLogPage] = useState(1);
    const [logTotal, setLogTotal] = useState(0);
    const [logPageSize, setLogPageSize] = useState(LOG_PAGE_SIZE);
    const [payoutItems, setPayoutItems] = useState([]);
    const [payoutLoading, setPayoutLoading] = useState(true);
    const [payoutPage, setPayoutPage] = useState(1);
    const [payoutTotal, setPayoutTotal] = useState(0);
    const [payoutPageSize, setPayoutPageSize] = useState(LOG_PAGE_SIZE);
    const [resendingIds, setResendingIds] = useState([]);
    const displaySelectedId = selectedId ?? resolveSelectedId(methods);
    // 1. Ensure selectedId is not nullish
// 2. Coerce both to strings so '123' safely matches 123
    const selectionDirty =
        selectedId !== null &&
        String(selectedId) !== String(serverSelectedId);
    
    const updateUserPaymentContext = (nextMethods, nextSelectedId) => {
        if (!user) return;
        const hasMethod = Array.isArray(nextMethods) && nextMethods.length > 0;
        const resolvedSelectedId = nextSelectedId ?? resolveSelectedId(nextMethods);
        const nextUser = {
            ...user,
            payment_method: hasMethod,
            sagent_payment_method_id: resolvedSelectedId || null,
            payment_method_skipped: hasMethod ? false : user.payment_method_skipped,
        };
        setUser(nextUser);
    };

    const handleSkip = () => {
        navigate("/dashboard");
    };

    const fetchMethods = async () => {
        if (!user?.access_token || !user?.id) {
            setMethodsLoading(false);
            return;
        }

        setMethodsLoading(true);

        try {
            const query = buildQuery({
                access_token: user.access_token,
                sagent_id: user.id,
            });

            const res = await fetch(
                `${api.main}api/disbursement/payment_methods?${query}`
            );
            if (!res.ok) {
                throw new Error("Failed to load payment methods");
            }
            const data = await res.json();
            const list = Array.isArray(data?.data) ? data.data : [];
            setMethods(list);
            const resolvedSelected = resolveSelectedId(list);
            setServerSelectedId(resolvedSelected);
            setSelectedId(resolvedSelected || null);
            updateUserPaymentContext(list, resolvedSelected);
        } catch (err) {
            message.error(err.message || "Unable to load payment methods");
        } finally {
            setMethodsLoading(false);
        }
    };

    const fetchLog = async (page = 1) => {
        if (!user?.access_token || !user?.id) {
            setLogLoading(false);
            return;
        }
        setLogLoading(true);
        try {
            const query = buildQuery({
                access_token: user.access_token,
                sagent_id: user.id,
                page,
            });

            const res = await fetch(
                `${api.main}api/disbursement/disbursement_log?${query}`
            );
            if (!res.ok) {
                throw new Error("Failed to load disbursement log");
            }
            const data = await res.json();
            const list = Array.isArray(data?.data) ? data.data : [];
            setLogItems(list);
            setLogPage(data?.page || page);
            setLogTotal(Number(data?.total || 0));
            setLogPageSize(Number(data?.page_size || LOG_PAGE_SIZE));
        } catch (err) {
            message.error(err.message || "Unable to load disbursement log");
        } finally {
            setLogLoading(false);
        }
    };
    const fetchPayouts = async (page = 1) => {
        if (!user?.access_token || !user?.id) {
            setPayoutLoading(false);
            return;
        }
        setPayoutLoading(true);
        try {
            const query = buildQuery({
                access_token: user.access_token,
                sagent_id: user.id,
                page,
            });

            const res = await fetch(
                `${api.main}api/disbursement/get_payouts?${query}`
            );
            if (!res.ok) {
                throw new Error("Failed to load payouts");
            }
            const data = await res.json();
            const list = Array.isArray(data?.data) ? data.data : [];
            setPayoutItems(list);
            setPayoutPage(data?.page || page);
            setPayoutTotal(Number(data?.total || 0));
            setPayoutPageSize(Number(data?.page_size || LOG_PAGE_SIZE));
        } catch (err) {
            message.error(err.message || "Unable to load payouts");
        } finally {
            setPayoutLoading(false);
        }
    };

    useEffect(() => {
        fetchMethods();
        fetchPayouts(1);
    }, [user?.access_token, user?.id]);

    const paymentTypeFields = useMemo(() => {
        return TYPE_FIELDS[form.payment_type] || [];
    }, [form.payment_type]);

    const requiredFieldNames = useMemo(() => {
        return [
            ...COMMON_FIELDS.map((field) => field.name),
            ...paymentTypeFields.map((field) => field.name),
        ];
    }, [paymentTypeFields]);

    const handleFieldChange = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const missing = requiredFieldNames.filter((key) => {
            const value = form[key];
            return !value || String(value).trim() === "";
        });
        if (missing.length) {
            message.error("Please fill out all required fields.");
            return false;
        }
        return true;
    };

    const resetForm = () => {
        setForm((prev) => ({
            ...defaultForm,
            payment_type: prev.payment_type || "ach",
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!user?.access_token || !user?.id) return;
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            const payload = {
                access_token: user.access_token,
                sagent_id: user.id,
                payment_type: form.payment_type,
            };
            requiredFieldNames.forEach((key) => {
                payload[key] = form[key];
            });
            
            const res = await fetch(
                `${api.main}api/disbursement/register_payment_method`,
                {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify(payload),
                }
            );
            if (!res.ok) {
                throw new Error("Failed to register payment method");
            }
            message.success("Payment method added successfully.");
            resetForm();
            setShowForm(false);
            await fetchMethods();
        } catch (err) {
            message.error(err.message || "Unable to add payment method");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSelectPreferred = async () => {
        if (selectedId === null || selectedId === undefined || !user?.access_token || !user?.id) {
            console.warn("Save blocked by guard rail. One or more required fields are missing.");
            return;
        }

        setSavingSelection(true);

        try {
            const res = await fetch(
                `${api.main}api/disbursement/select_payment_method`,
                {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        access_token: user.access_token,
                        sagent_id: user.id,
                        sagent_payment_method_id: selectedId,
                    }),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to update preferred method");
            }

            message.success("Preferred payment method updated.");
            setServerSelectedId(selectedId);

            const updated = methods.map((item) => ({
                ...item,
                selected: item.sagent_payment_method_id === selectedId ? 1 : 0,
            }));

            setMethods(updated);
            updateUserPaymentContext(updated, selectedId);
        } catch (err) {
            message.error(err.message || "Unable to update preferred method");
        } finally {
            setSavingSelection(false);
        }
    };

    const handleDelete = async (methodId) => {
        if (!methodId || !user?.access_token || !user?.id) return;
        if (!window.confirm("Delete this payment method?")) return;
        setDeletingIds((prev) => [...prev, methodId]);

        try {
            const res = await fetch(
                `${api.main}api/disbursement/delete_payment_method`,
                {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({
                        access_token: user.access_token,
                        sagent_id: user.id,
                        sagent_payment_method_id: methodId,
                    }),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to delete payment method");
            }

            message.success("Payment method deleted.");
            await fetchMethods();
        } catch (err) {
            message.error(err.message || "Unable to delete payment method");
        } finally {
            setDeletingIds((prev) => prev.filter((id) => id !== methodId));
        }
    };

    const handleResend = async (record) => {
        if (!record?.disbursement_id || !user?.access_token || !user?.id) return;
        setResendingIds((prev) => [...prev, record.disbursement_id]);

        try {
            const res = await fetch(`${api.main}api/disbursement/resend_payout`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    access_token: user.access_token,
                    sagent_id: user.id,
                    disbursement_id: record.disbursement_id,
                }),
            });

            if (!res.ok) {
                throw new Error("Failed to resend payout");
            }

            message.success("Payout resent successfully.");
            fetchLog(logPage);
        } catch (err) {
            message.error(err.message || "Unable to resend payout");
        } finally {
            setResendingIds((prev) =>
                prev.filter((id) => id !== record.disbursement_id)
            );
        }
    };

    const logColumns = [
        {
            title: "Disbursement ID",
            dataIndex: "disbursement_id",
            key: "disbursement_id",
            width: 150,
            render: (value) => value || "—",
        },
        {
            title: "Payment Method ID",
            dataIndex: "sagent_payment_method_id",
            key: "sagent_payment_method_id",
            width: 150,
            render: (value) => value || "—",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            render: (value) => formatAmount(value),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 140,
            render: (value) => formatStatus(value),
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (value) =>
                value ? new Date(value).toLocaleDateString() : "—",
        },
        {
            title: "Updated",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 150,
            render: (value) =>
                value ? new Date(value).toLocaleDateString() : "—",
        },
        {
            title: "Actions",
            key: "actions",
            width: 140,
            render: (_, record) => {
                const canResend = record?.status === "Invalid Payment Method";
                if (!canResend) return "—";
                const resending = resendingIds.includes(record.disbursement_id);
                return (
                    <button
                        className="btn btn-secondary"
                        onClick={() => handleResend(record)}
                        disabled={resending}
                    >
                        {resending ? "Resending..." : "Resend"}
                    </button>
                );
            },
        },
    ];

    const payoutColumns = [
        {
            title: "Payout ID",
            dataIndex: "sagent_payout_id",
            key: "sagent_payout_id",
            width: 150,
            render: (value) => value || "—",
        },
        {
            title: "Payment Type",
            dataIndex: "payment_type",
            key: "payment_type",
            width: 150,
            render: (value) => value || "—",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            render: (value) => formatAmount(value),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 140,
            render: (value) => formatStatus(value),
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (value) =>
                value ? new Date(value).toLocaleDateString() : "—",
        },
        {
            title: "Updated",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 150,
            render: (value) =>
                value ? new Date(value).toLocaleDateString() : "—",
        },
        {
            title: "Actions",
            key: "actions",
            width: 140,
            render: (_, record) => {
                const canResend = record?.status === "Invalid Payment Method";
                if (!canResend) return "—";
                const resending = resendingIds.includes(record.disbursement_id);
                return (
                    <button
                        className="btn btn-secondary"
                        onClick={() => handleResend(record)}
                        disabled={resending}
                    >
                        {resending ? "Resending..." : "Resend"}
                    </button>
                );
            },
        },
    ];


    return (
        <div className="payment-setup-page">
            <div className="page-header">
                <div>
                    <p className="page-eyebrow">Earnings</p>
                    <h2>Payouts</h2>
                    <p className="section-subtitle">
                        Add a payout method, select your preferred option, and review recent
                        earnings.
                    </p>
                </div>
                <div className="payment-setup-header-actions">
                    <button className="btn btn-secondary" onClick={handleSkip}>
                        Skip for now
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowForm((prev) => !prev)}
                    >
                        {showForm ? "Hide Form" : "Add New Payment Method"}
                    </button>
                </div>
            </div>

            {showForm && (
                <section className="page-section payment-setup-panel">
                    <div className="section-heading">
                        <div>
                            <p className="section-eyebrow">Add Method</p>
                            <h3>Add new payment method</h3>
                            <p className="section-subtitle">
                                Complete the details for the payout method you want to add.
                            </p>
                        </div>
                    </div>
                    <form className="payment-setup-form" onSubmit={handleSubmit}>
                        <div className="payment-form-grid">
                            <div className="payment-form-field">
                                <label>Payment type *</label>
                                <div className="payment-type-options">
                                    {["ach", "debit", "paypal", "venmo"].map((option) => (
                                        <label key={option} className="payment-type-option">
                                            <input
                                                type="radio"
                                                name="payment_type"
                                                value={option}
                                                checked={form.payment_type === option}
                                                onChange={(event) =>
                                                    handleFieldChange("payment_type", event.target.value)
                                                }
                                            />
                                            <span>{option.toUpperCase()}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="payment-form-grid">
                            {COMMON_FIELDS.map((field) => (
                                <div className="payment-form-field" key={field.name}>
                                    <label htmlFor={field.name}>{field.label} *</label>
                                    <input
                                        id={field.name}
                                        type={field.type}
                                        value={form[field.name]}
                                        onChange={(event) =>
                                            handleFieldChange(field.name, event.target.value)
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="payment-form-divider">
                            {form.payment_type.toUpperCase()} details
                        </div>
                        <div className="payment-form-grid">
                            {paymentTypeFields.map((field) => (
                                <div className="payment-form-field" key={field.name}>
                                    <label htmlFor={field.name}>{field.label} *</label>
                                    {field.type === "select" ? (
                                        <select
                                            id={field.name}
                                            value={form[field.name]}
                                            onChange={(event) =>
                                                handleFieldChange(field.name, event.target.value)
                                            }
                                        >
                                            <option value="">Select</option>
                                            {field.options?.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            id={field.name}
                                            type={field.type}
                                            value={form[field.name]}
                                            onChange={(event) =>
                                                handleFieldChange(field.name, event.target.value)
                                            }
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="payment-form-actions">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    resetForm();
                                    setShowForm(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : "Add Payment Method"}
                            </button>
                        </div>
                    </form>
                </section>
            )}

            <section className="page-section payment-setup-panel">
                <div className="section-heading">
                    <div>
                        <p className="section-eyebrow">Payment Methods</p>

                        <h3>Preferred payout method</h3>
                        <p className="section-subtitle">
                            Choose the method we should use for your earnings.
                        </p>
                    </div>
                    <div className="payment-setup-actions">
                        <button
                            className="btn btn-secondary"
                            onClick={fetchMethods}
                            disabled={methodsLoading}
                        >
                            Refresh
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={handleSelectPreferred}
                            disabled={!selectionDirty || savingSelection}
                        >
                            {savingSelection ? "Saving..." : "Save Preferred Method"}
                        </button>
                    </div>
                </div>
                {methodsLoading ? (
                    <div className="payment-setup-empty">Loading payment methods...</div>
                ) : methods.length === 0 ? (
                    <div className="payment-setup-empty">
                        No payment methods on file. Add one to start receiving payouts.
                    </div>
                ) : (
                    <div className="payment-method-grid">
                        {methods.map((method) => {
                            const isSelected = selectedId !== null
                                ? method.sagent_payment_method_id === selectedId
                                : Number(method.selected) === 1;

                            const isDeleting = deletingIds.includes(method.sagent_payment_method_id);
                            return (
                                <div
                                    key={method.id}
                                    className={`payment-method-tile${
                                        isSelected ? " is-selected" : ""
                                    }`}
                                >
                                    <div className="payment-method-header">
                                        <label className="payment-method-radio">
                                            <input
                                                type="radio"
                                                name="preferred-method"
                                                checked={isSelected}
                                                onChange={() => setSelectedId(method.sagent_payment_method_id)}
                                            />
                                            <span>
                                                {method.payment_type
                                                    ? method.payment_type.toUpperCase()
                                                    : "Payment Method"}
                                            </span>
                                        </label>
                                        {Number(method.selected) === 1 && (
                                            <span className="payment-method-tag">Preferred</span>
                                        )}
                                    </div>
                                    <div className="payment-method-body">
                                        <p className="payment-method-info">
                                            {method.payment_info || "—"}
                                        </p>
                                        <p className="payment-method-meta">
                                            Status: {formatStatus(method.status)}
                                        </p>
                                    </div>
                                    <div className="payment-method-actions">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => handleDelete(method.sagent_payment_method_id)}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting ? "Deleting..." : "Delete"}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
            <section className="page-section payment-setup-panel">
                <div className="section-heading">
                    <div>
                        <p className="section-eyebrow">Disbursement Log</p>
                        <h3>Payouts</h3>
                        <p className="section-subtitle">
                            Amounts are shown in USD.
                        </p>
                    </div>
                </div>
                <div className="payment-log-table">
                    <Table
                        columns={payoutColumns}
                        dataSource={payoutItems}
                        loading={payoutLoading}
                        pagination={false}
                        rowKey={(row) => row.sagent_payout_id || row.disbursement_id}
                    />
                </div>
                <div className="payment-log-footer">
                    <Pagination
                        current={payoutPage}
                        pageSize={payoutPageSize}
                        total={payoutTotal}
                        onChange={(page) => {
                            setPayoutPage(page);
                            fetchPayouts(page);
                        }}
                        showSizeChanger={false}
                    />
                </div>
            </section>
        </div>
    );
}